import { memo, useState } from 'react'
import { useI18n } from '../i18n/I18nContext'

interface AdvancedFiltersProps {
  minStars: number | null
  setMinStars: (value: number | null) => void
  maxStars: number | null
  setMaxStars: (value: number | null) => void
  minForks: number | null
  setMinForks: (value: number | null) => void
  maxForks: number | null
  setMaxForks: (value: number | null) => void
  dateRange: 'all' | 'today' | 'week' | 'month' | 'year'
  setDateRange: (value: 'all' | 'today' | 'week' | 'month' | 'year') => void
  hasHomepage: boolean | null
  setHasHomepage: (value: boolean | null) => void
  isArchived: boolean | null
  setIsArchived: (value: boolean | null) => void
  onReset: () => void
  theme: 'light' | 'dark'
}

export const AdvancedFilters = memo(function AdvancedFilters({
  minStars,
  setMinStars,
  maxStars,
  setMaxStars,
  minForks,
  setMinForks,
  maxForks,
  setMaxForks,
  dateRange,
  setDateRange,
  hasHomepage,
  setHasHomepage,
  isArchived,
  setIsArchived,
  onReset,
  theme: _theme
}: AdvancedFiltersProps) {
  const { t } = useI18n()
  const [isExpanded, setIsExpanded] = useState(false)

  const hasActiveFilters =
    minStars !== null ||
    maxStars !== null ||
    minForks !== null ||
    maxForks !== null ||
    dateRange !== 'all' ||
    hasHomepage !== null ||
    isArchived !== null

  return (
    <div className="advanced-filters-container">
      <button
        className={`advanced-filters-toggle ${hasActiveFilters ? 'has-filters' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-label={isExpanded ? t('advanced.toggle.hide') : t('advanced.toggle.show')}
      >
        <span>{t('advanced.title')}</span>
        {hasActiveFilters && <span className="filter-badge">{Object.values({
          minStars, maxStars, minForks, maxForks, dateRange, hasHomepage, isArchived
        }).filter(v => v !== null && v !== 'all').length}</span>}
        <span className="toggle-icon">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {isExpanded && (
        <div className="advanced-filters-content animate-slideIn">
          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">{t('advanced.stars')}</label>
              <div className="range-inputs">
                <input
                  type="number"
                  placeholder={t('advanced.placeholder.min')}
                  value={minStars ?? ''}
                  onChange={(e) => setMinStars(e.target.value ? parseInt(e.target.value, 10) : null)}
                  min="0"
                  className="filter-input"
                />
                <span className="range-separator">-</span>
                <input
                  type="number"
                  placeholder={t('advanced.placeholder.max')}
                  value={maxStars ?? ''}
                  onChange={(e) => setMaxStars(e.target.value ? parseInt(e.target.value, 10) : null)}
                  min="0"
                  className="filter-input"
                />
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">{t('advanced.forks')}</label>
              <div className="range-inputs">
                <input
                  type="number"
                  placeholder={t('advanced.placeholder.min')}
                  value={minForks ?? ''}
                  onChange={(e) => setMinForks(e.target.value ? parseInt(e.target.value, 10) : null)}
                  min="0"
                  className="filter-input"
                />
                <span className="range-separator">-</span>
                <input
                  type="number"
                  placeholder={t('advanced.placeholder.max')}
                  value={maxForks ?? ''}
                  onChange={(e) => setMaxForks(e.target.value ? parseInt(e.target.value, 10) : null)}
                  min="0"
                  className="filter-input"
                />
              </div>
            </div>

            <div className="filter-group">
              <label className="filter-label">{t('advanced.updated.label')}</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as 'all' | 'today' | 'week' | 'month' | 'year')}
                className="filter-select"
              >
                <option value="all">{t('advanced.date.all')}</option>
                <option value="today">{t('advanced.date.today')}</option>
                <option value="week">{t('advanced.date.week')}</option>
                <option value="month">{t('advanced.date.month')}</option>
                <option value="year">{t('advanced.date.year')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">{t('advanced.options')}</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={hasHomepage === true}
                    onChange={(e) => setHasHomepage(e.target.checked ? true : null)}
                  />
                  <span>{t('advanced.withHomepage')}</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isArchived === false}
                    onChange={(e) => setIsArchived(e.target.checked ? false : null)}
                  />
                  <span>{t('advanced.activeOnly')}</span>
                </label>
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="filter-actions">
              <button type="button" onClick={onReset} className="button secondary">
                {t('advanced.reset')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
})
